import { Paperclip, ArrowLeft } from '@untitled-ui/icons-react';
import dayjs from 'dayjs';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import logo from '@/assets/logo.png';
import { IconSendPlane } from '@/components/icons';
import BaseSkeleton from '@/components/ui/BaseSkeleton';
import { IHelpdeskTicket } from '@/types/helpdesk';

import { useCreateHelpdeskTicketMessage } from '../api/use-create-helpdesk-ticket-message';
import { useGetHelpdeskTicketMessages } from '../api/use-get-helpdesk-ticket-messages';

type Props = {
  ticket: IHelpdeskTicket;
  onBackButtonClick: () => void;
};

const AUTHOR_ID = 12;

const Chat = ({ ticket, onBackButtonClick }: Props) => {
  const { t } = useTranslation('communicationPage');
  const [messageText, setMessageText] = useState('');
  const [isConversationStarted, setIsConversationStarted] = useState(false);

  const messagesQuery = useGetHelpdeskTicketMessages(ticket?.id);

  const createMessageMutation = useCreateHelpdeskTicketMessage(ticket?.id);

  useEffect(() => {
    setIsConversationStarted(false);
  }, [ticket]);

  const handleSubmitMessage = async (e: FormEvent) => {
    e.preventDefault();
    createMessageMutation.mutate(
      {
        subject: `Contract #${ticket.xContractNumber[0]}`,
        body: messageText,
        messageType: 'comment',
        partnerIds: ticket.messagePartnerIds,
        authorId: AUTHOR_ID,
      },
      {
        onSuccess: () => {
          messagesQuery.refetch();
          setMessageText('');
        },
      },
    );
  };

  const handleMessageTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMessageText(e.target.value);
  };

  const handleStartButtonClick = () => {
    setIsConversationStarted(true);
  };

  return (
    <div className="flex-1 flex flex-col h-full md:h-auto">
      <div className="bg-white h-[82px] mb-5 rounded-[32px] px-6 py-4 flex items-center gap-3">
        <button onClick={onBackButtonClick} className="w-6 h-6 stroke-gray-10">
          <ArrowLeft />
        </button>
        <img src={logo} className="w-12 h-12 object-contain" alt="logo" />
        <div className="flex flex-1 flex-col justify-between">
          <span className="text-2xl font-medium text-gray-10">{t('customerSupport')}</span>
          <span className="text-sm font-normal text-[#595959]">{ticket.displayName}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto flex flex-col justify-between gap-6 p-6 bg-white rounded-t-[32px]">
        {messagesQuery.isLoading ? (
          <BaseSkeleton className="h-full" />
        ) : messagesQuery.data?.length || isConversationStarted ? (
          <>
            <div className="flex-1 overflow-y-auto flex flex-col flex-grow gap-4">
              {messagesQuery.data
                .filter((message) => message.body)
                .map((message) =>
                  message.authorId[0] === AUTHOR_ID ? (
                    <div key={message.id} className="flex min-w-1/2 self-end items-end gap-1">
                      <span className="text-sm text-gray-7">{dayjs(ticket.createDate).format('hh:mm')}</span>
                      <div
                        dangerouslySetInnerHTML={{ __html: message.body }}
                        className="text-[#595959] leading-6 p-3 text-sm font-normal bg-[#EBF163] rounded-t-[16px] rounded-l-[16px]"
                      />
                    </div>
                  ) : (
                    <div key={message.id} className="flex min-w-1/2 self-start items-end gap-1">
                      <div
                        dangerouslySetInnerHTML={{ __html: message.body }}
                        className="p-3 leading-6 text-[#595959] text-sm font-normal bg-[#F5F5F5] rounded-t-[16px] rounded-r-[16px]"
                      />
                      <span className="text-sm text-gray-7">{dayjs(ticket.createDate).format('hh:mm')}</span>
                    </div>
                  ),
                )}
            </div>
            <div className="flex gap-2 items-center">
              <button className="w-8 h-8 text-[#B9B9B9]">
                <Paperclip />
              </button>
              <form onSubmit={handleSubmitMessage} className="flex-1 flex border p-4 justify-between gap-4 border-[#DFDFDF] rounded-[14px]">
                <input
                  onChange={handleMessageTextChange}
                  value={messageText}
                  placeholder={t('writeYourProblem')}
                  className="flex-1 w-full outline-none text-base font-normal"
                />
                {messageText && (
                  <button type="submit" className="text-brand-green">
                    <IconSendPlane size="sm" />
                  </button>
                )}
              </form>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 flex flex-col items-center justify-center">
              <span className="py-2 px-4 bg-[#B9B9B9] text-white text-xs font-normal rounded-md">{t('noMessages')}</span>
            </div>
            <button onClick={handleStartButtonClick} className="h-14 w-full rounded-[30px] bg-brand-green flex items-center justify-center">
              <span>{t('start')}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Chat;
